import type { AxiosRequestConfig } from 'axios';
import type { WhatsAppAnalytics } from '@shared/types/whatsapp-analytics';
import type { ChannelReport } from '@shared/types/channel-reports';
import type {
  ChannelCatalogue,
  ManagedChannelType,
  ManagedChannelCode,
  SaveChannel,
} from '@shared/types/domain';
import { apiClient } from './client';
import type {
  WhatsAppCustomerContext,
  CustomerTicket,
  CustomerInvoice,
  CustomerWonItem,
  CustomerJob,
  CustomerJobEvent,
} from '@shared/types/whatsapp-customer-context';
import type {
  DashboardSummary,
  DashboardSatisfaction,
  PagedResult,
  Conversation,
  WhatsAppMessagePage,
  Ticket,
  LegacyTicketDetail,
  LegacyTicketListItem,
  TicketSummary,
  TicketLookups,
  CallEnquiry,
  CallEnquiryLookups,
  CreateCallEnquiry,
  CreatedCallEnquiry,
  KnowledgeAnalytics,
  KnowledgeArticleSummary,
  KnowledgeArticle,
  KnowledgeArticleVersionCompare,
  KnowledgeArticleVersionDetail,
  KnowledgeAudience,
  KnowledgeCategory,
  KnowledgeFeedback,
  KnowledgeGap,
  KnowledgeGapOccurrencePage,
  KnowledgeGapPage,
  KnowledgeGapStatus,
  KnowledgeLanguage,
  KnowledgeLanguageSyncApplyResult,
  KnowledgeLanguageSyncPreview,
  KnowledgeLookup,
  KnowledgePlaygroundResponse,
  KnowledgeAgentAssistApplyResult,
  KnowledgeAgentAssistSuggestion,
  ComposerAiPolishAction,
  ComposerAiPolishContextMessage,
  ComposerAiPolishResult,
  KnowledgeReviewGuard,
  KnowledgeTag,
  KnowledgeWebhookDelivery,
  KnowledgeWebhookDeliveryPage,
  KnowledgeWebhookSubscription,
  SaveKnowledgeWebhookSubscription,
  IssuedKnowledgeConnectorCredential,
  KnowledgeConnector,
  KnowledgeConnectorCredential,
  KnowledgeConnectorIngestItem,
  KnowledgeConnectorIngestResponse,
  KnowledgeConnectorRunPage,
  KnowledgeMcpSourceCatalog,
  KnowledgeMcpSourceSyncResponse,
  KnowledgeConnectorScope,
  SaveKnowledgeConnector,
  LiveChatAgentMapping,
  LiveChatAssignableUser,
  LiveChatCannedResponse,
  WhatsAppAgentMapping,
  WhatsAppActionReason,
  WhatsAppAgentOption,
  WhatsAppAssignableUser,
  WhatsAppProfile,
  WhatsAppOperationConversationResult,
  WhatsAppPhoneFormat,
  ErsaStartMessageResult,
  ErsaStartMessageAuditPage,
  WhatsAppAdminAgent,
  WhatsAppAdminAiPolishAudit,
  WhatsAppAdminAudit,
  WhatsAppAdminClient,
  WhatsAppAdminDashboard,
  WhatsAppAdminOperationAudit,
  WhatsAppAdminPage,
  WhatsAppAdminProfile,
  WhatsAppAdminReason,
  WhatsAppMessageShortcut,
  WhatsAppReopenTemplate,
  EscalationPage,
  WhatsAppSlaPolicy,
  AssignableSystemAdministrator,
  IdentityPagedResult,
  IdentityPermission,
  IdentityRole,
  IdentityUser,
  TeamRecord,
  EntityRecord,
  ChannelRecord,
  SlaPolicy,
  ReportResult,
  SettingsRecord,
  IssuedKnowledgeMcpCredential,
  KnowledgeMcpConnector,
  KnowledgeMcpConnectorAnalytics,
  KnowledgeMcpCredential,
  SaveKnowledgeMcpConnector,
  ApplyKnowledgeLanguageSyncRequest,
} from '@shared/types/domain';

export const queryKeys = {
  dashboard: ['dashboard'] as const,
  conversations: ['conversations'] as const,
  liveChatAgentMappings: ['live-chat', 'agent-mappings'] as const,
  liveChatAssignableUsers: ['live-chat', 'assignable-users'] as const,
  liveChatCannedResponses: ['live-chat', 'canned-responses'] as const,
  whatsAppProfiles: ['whatsapp', 'profiles'] as const,
  whatsAppMessageShortcuts: (waProfileId: number) =>
    ['whatsapp', 'message-shortcuts', waProfileId] as const,
  whatsAppActionReasons: (isEndReason: boolean) =>
    ['whatsapp', 'action-reasons', isEndReason] as const,
  whatsAppAgents: ['whatsapp', 'agents'] as const,
  whatsAppAgentMappings: ['whatsapp', 'agent-mappings'] as const,
  whatsAppAssignableUsers: ['whatsapp', 'assignable-users'] as const,
  ersaStartMessageAudits: (params: Record<string, string | number | boolean>) =>
    ['whatsapp', 'ersa', 'audits', params] as const,
  whatsAppAdmin: (resource: string, params?: Record<string, unknown>) =>
    ['whatsapp', 'admin', resource, params ?? {}] as const,
  tickets: ['tickets'] as const,
  calls: ['calls'] as const,
  articles: ['articles'] as const,
  knowledgeWebhookSubscriptions: ['knowledge', 'connectors', 'webhooks'] as const,
  knowledgeWebhookDeliveries: ['knowledge', 'connectors', 'webhook-deliveries'] as const,
  knowledgeConnectors: ['knowledge', 'connectors', 'catalogue'] as const,
  knowledgeConnectorCredentials: (id: string) =>
    ['knowledge', 'connectors', id, 'credentials'] as const,
  knowledgeConnectorRuns: ['knowledge', 'connectors', 'runs'] as const,
  knowledgeMcpConnectors: ['knowledge', 'mcp-connectors'] as const,
  knowledgeMcpCredentials: (id: string) =>
    ['knowledge', 'mcp-connectors', id, 'credentials'] as const,
  knowledgeMcpConnectorAnalytics: (id: string, fromUtc: string, toUtc: string) =>
    ['knowledge', 'mcp-connectors', id, 'analytics', fromUtc, toUtc] as const,
  knowledgeRestConnectors: ['knowledge', 'rest-connectors'] as const,
  knowledgeRestCredentials: (id: string) =>
    ['knowledge', 'rest-connectors', id, 'credentials'] as const,
  knowledgeRestConnectorAnalytics: (id: string, fromUtc: string, toUtc: string) =>
    ['knowledge', 'rest-connectors', id, 'analytics', fromUtc, toUtc] as const,
  knowledgeGaps: (params: Record<string, string | number>) =>
    ['knowledge', 'gaps', params] as const,
  escalations: ['escalations'] as const,
  whatsAppSla: ['sla', 'whatsapp'] as const,
  administration: (resource: string) => ['administration', resource] as const,
  sla: ['sla'] as const,
  settings: ['settings'] as const,
};
const get = async <T>(url: string, config?: AxiosRequestConfig) =>
  (await apiClient.get<T>(url, config)).data;
const send = async <T>(method: 'post' | 'patch' | 'put', url: string, data: unknown) =>
  (await apiClient[method]<T>(url, data)).data;
const remove = async (url: string) => {
  await apiClient.delete(url);
};

export const helixApi = {
  channelReport: (
    channel: 'tickets' | 'calls' | 'live-chat',
    params: Record<string, string>,
    signal?: AbortSignal,
  ) => get<ChannelReport>(`/reports/${channel}`, { params, ...(signal ? { signal } : {}) }),
  dashboard: () => get<DashboardSummary>('/dashboard/summary'),
  dashboardSatisfaction: (signal?: AbortSignal) =>
    get<DashboardSatisfaction>('/dashboard/satisfaction/live-chat', signal ? { signal } : {}),
  conversations: (params?: Record<string, string | number | boolean>, signal?: AbortSignal) =>
    get<PagedResult<Conversation>>('/conversations', { params, ...(signal ? { signal } : {}) }),
  conversation: (id: string, signal?: AbortSignal) =>
    get<Conversation>(`/conversations/${id}`, signal ? { signal } : {}),
  whatsAppMessages: (id: string, cursor: string) =>
    get<WhatsAppMessagePage>(`/conversations/${id}/messages`, { params: { cursor } }),
  whatsAppCustomerContext: (id: string, signal?: AbortSignal) =>
    get<WhatsAppCustomerContext>(
      `/conversations/${encodeURIComponent(id)}/customer-context`,
      signal ? { signal } : {},
    ),
  whatsAppCustomerTickets: (id: string, signal?: AbortSignal) =>
    get<CustomerTicket[]>(
      `/conversations/${encodeURIComponent(id)}/customer-context/tickets`,
      signal ? { signal } : {},
    ),
  whatsAppCustomerInvoices: (id: string, signal?: AbortSignal) =>
    get<CustomerInvoice[]>(
      `/conversations/${encodeURIComponent(id)}/customer-context/invoices`,
      signal ? { signal } : {},
    ),
  whatsAppCustomerWonItems: (id: string, signal?: AbortSignal) =>
    get<CustomerWonItem[]>(
      `/conversations/${encodeURIComponent(id)}/customer-context/won-items`,
      signal ? { signal } : {},
    ),
  whatsAppCustomerJobs: (id: string, signal?: AbortSignal) =>
    get<CustomerJob[]>(
      `/conversations/${encodeURIComponent(id)}/customer-context/jobs`,
      signal ? { signal } : {},
    ),
  whatsAppCustomerJobTimeline: (id: string, jobId: number, signal?: AbortSignal) =>
    get<CustomerJobEvent[]>(
      `/conversations/${encodeURIComponent(id)}/customer-context/jobs/${jobId}/timeline`,
      signal ? { signal } : {},
    ),
  markConversationRead: (id: string) =>
    send<Conversation>('post', `/conversations/${id}/read`, undefined),
  whatsAppReopenTemplates: (waProfileId: number) =>
    get<WhatsAppReopenTemplate[]>('/whatsapp/reopen-templates', { params: { waProfileId } }),
  reopenWhatsAppConversation: (
    id: string,
    data: { templateId: number; language: 'en' | 'ar'; parameters: string[] },
  ) => send<Conversation>('post', `/conversations/${encodeURIComponent(id)}/reopen`, data),
  sendMessage: (id: string, data: { body: string; kind?: 'text' | 'internal_note' }) =>
    send<Conversation['messages'][number]>('post', `/conversations/${id}/messages`, data),
  sendWhatsAppAttachment: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return send<Conversation['messages'][number]>(
      'post',
      `/conversations/${encodeURIComponent(id)}/attachments`,
      formData,
    );
  },
  sendWhatsAppVoiceMessage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return send<Conversation['messages'][number]>(
      'post',
      `/conversations/${encodeURIComponent(id)}/voice-messages`,
      formData,
    );
  },
  conversationAction: (
    id: string,
    data: { action: string; assignee?: string; reasonId?: number },
  ) => send<Conversation>('post', `/conversations/${id}/action`, data),
  liveChatConversations: (params?: Record<string, string | number | boolean>) =>
    get<PagedResult<Conversation>>('/live-chat/conversations', { params }),
  liveChatConversation: (id: string) =>
    get<Conversation>(`/live-chat/conversations/${encodeURIComponent(id)}`),
  liveChatCannedResponses: () => get<LiveChatCannedResponse[]>('/live-chat/canned-responses'),
  sendLiveChatMessage: (id: string, data: { body: string; kind?: 'text' | 'internal_note' }) =>
    send<Conversation['messages'][number]>(
      'post',
      `/live-chat/conversations/${encodeURIComponent(id)}/messages`,
      data,
    ),
  createKnowledgeAgentAssistSuggestion: async (data: {
    channel: 'live_chat' | 'whatsapp';
    conversationId: string;
  }) =>
    (
      await apiClient.post<KnowledgeAgentAssistSuggestion>(
        '/knowledge/agent-assist/suggestions',
        data,
        { timeout: 30_000 },
      )
    ).data,
  applyKnowledgeAgentAssistSuggestion: (
    suggestionId: string,
    data: {
      channel: 'live_chat' | 'whatsapp';
      conversationId: string;
      action: 'use' | 'replace' | 'dismiss';
    },
  ) =>
    send<KnowledgeAgentAssistApplyResult>(
      'post',
      `/knowledge/agent-assist/suggestions/${encodeURIComponent(suggestionId)}/apply`,
      data,
    ),
  liveChatConversationAction: (id: string, data: { action: string; assignee?: string }) =>
    send<Conversation>('post', `/live-chat/conversations/${encodeURIComponent(id)}/action`, data),
  uploadComposerAttachment: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return send<{ fileName: string; url: string }>('post', '/composer/attachments', formData);
  },
  translateComposerText: (text: string) =>
    send<{ translation: string; sourceLanguage: 'ar' | 'en'; targetLanguage: 'ar' | 'en' }>(
      'post',
      '/composer/translate',
      { text },
    ),
  polishComposerMessage: (data: {
    channel: 'live_chat' | 'whatsapp';
    conversationId: string;
    waProfileId?: number;
    draft: string;
    contextMessages: ComposerAiPolishContextMessage[];
  }) => send<ComposerAiPolishResult>('post', '/composer/ai-polish', data),
  recordComposerAiPolishAction: (
    logId: number,
    data: {
      channel: 'live_chat' | 'whatsapp';
      requestId: string;
      action: 'Replace' | 'Send';
      actualResponse: string;
    },
  ) => send<ComposerAiPolishAction>('post', `/composer/ai-polish/${logId}/actions`, data),
  liveChatAgentMappings: () => get<LiveChatAgentMapping[]>('/live-chat/agent-mappings'),
  liveChatAssignableUsers: () => get<LiveChatAssignableUser[]>('/live-chat/assignable-users'),
  createLiveChatAgentMapping: (data: {
    externalAdministratorId: number;
    liveChatAgentEmail: string;
  }) => send<LiveChatAgentMapping>('post', '/live-chat/agent-mappings', data),
  updateLiveChatAgentMapping: (
    id: string,
    data: { externalAdministratorId: number; liveChatAgentEmail: string },
  ) => send<LiveChatAgentMapping>('put', `/live-chat/agent-mappings/${id}`, data),
  deleteLiveChatAgentMapping: (id: string) => remove(`/live-chat/agent-mappings/${id}`),
  whatsAppProfiles: () => get<WhatsAppProfile[]>('/whatsapp/profiles'),
  whatsAppMessageShortcuts: (waProfileId: number) =>
    get<WhatsAppMessageShortcut[]>('/whatsapp/message-shortcuts', { params: { waProfileId } }),
  formatWhatsAppPhone: (data: { phone: string; language: 'en' | 'ar' }) =>
    send<WhatsAppPhoneFormat>('post', '/whatsapp/format-phone', data),
  startErsaFlow: (data: {
    phone: string;
    customerName?: string;
    language: 'en' | 'ar';
    customerType: 'bank' | 'insurance';
  }) => send<ErsaStartMessageResult>('post', '/whatsapp/ersa/start-flow', data),
  ersaStartMessageAudits: (params?: Record<string, string | number | boolean>) =>
    get<ErsaStartMessageAuditPage>('/whatsapp/ersa/audits', { params }),
  whatsAppAdminDashboard: () => get<WhatsAppAdminDashboard>('/whatsapp/admin/dashboard'),
  whatsAppDashboardAnalytics: (params: Record<string, string | number>, signal?: AbortSignal) =>
    get<WhatsAppAnalytics>('/whatsapp/admin/dashboard/analytics', {
      params,
      ...(signal ? { signal } : {}),
    }),
  whatsAppAdminAgents: (params?: Record<string, string | number | boolean>) =>
    get<WhatsAppAdminPage<WhatsAppAdminAgent>>('/whatsapp/admin/agents', { params }),
  updateWhatsAppAdminAgent: (
    id: number,
    data: Pick<WhatsAppAdminAgent, 'isAdmin' | 'isActive' | 'maxConcurrentChatsPerAgent'> & {
      expectedIsAdmin: boolean;
    },
  ) => send<void>('put', `/whatsapp/admin/agents/${id}`, data),
  whatsAppAdminClients: (params?: Record<string, string | number | boolean>) =>
    get<WhatsAppAdminPage<WhatsAppAdminClient>>('/whatsapp/admin/clients', { params }),
  whatsAppAdminProfiles: () => get<WhatsAppAdminProfile[]>('/whatsapp/admin/profiles'),
  updateWhatsAppAdminProfile: (
    id: number,
    data: Omit<WhatsAppAdminProfile, 'id' | 'name' | 'phoneNumber'>,
  ) => send<void>('put', `/whatsapp/admin/profiles/${id}`, data),
  whatsAppAdminReasons: () => get<WhatsAppAdminReason[]>('/whatsapp/admin/action-reasons'),
  updateWhatsAppAdminReason: (id: number, data: Omit<WhatsAppAdminReason, 'id'>) =>
    send<void>('put', `/whatsapp/admin/action-reasons/${id}`, data),
  whatsAppAdminMessageShortcuts: (waProfileId?: number) =>
    get<WhatsAppMessageShortcut[]>('/whatsapp/admin/message-shortcuts', {
      params: waProfileId ? { waProfileId } : undefined,
    }),
  createWhatsAppMessageShortcut: (data: Omit<WhatsAppMessageShortcut, 'id' | 'createdOn'>) =>
    send<WhatsAppMessageShortcut>('post', '/whatsapp/admin/message-shortcuts', data),
  updateWhatsAppMessageShortcut: (
    id: number,
    data: Omit<WhatsAppMessageShortcut, 'id' | 'createdOn'>,
  ) => send<WhatsAppMessageShortcut>('put', `/whatsapp/admin/message-shortcuts/${id}`, data),
  deleteWhatsAppMessageShortcut: (id: number, waProfileId: number) =>
    remove(`/whatsapp/admin/message-shortcuts/${id}?waProfileId=${waProfileId}`),
  whatsAppAdminAuditTrail: (params?: Record<string, string | number | boolean>) =>
    get<WhatsAppAdminPage<WhatsAppAdminAudit>>('/whatsapp/admin/audit-trail', { params }),
  whatsAppAdminAiPolishAudits: (params?: Record<string, string | number | boolean>) =>
    get<WhatsAppAdminPage<WhatsAppAdminAiPolishAudit>>('/whatsapp/admin/ai-polish-audits', {
      params,
    }),
  whatsAppAdminOperationAudits: (params?: Record<string, string | number | boolean>) =>
    get<WhatsAppAdminPage<WhatsAppAdminOperationAudit>>(
      '/whatsapp/admin/operation-message-audits',
      { params },
    ),
  startWhatsAppOperationConversation: (data: {
    waProfileId: number;
    phone: string;
    name?: string;
    language: 'en' | 'ar';
  }) =>
    send<WhatsAppOperationConversationResult>('post', '/whatsapp/operation-conversations', data),
  convertWhatsAppOperationConversation: (id: string, data: { language: 'en' | 'ar' }) =>
    send<WhatsAppOperationConversationResult>(
      'post',
      `/conversations/${encodeURIComponent(id)}/operation`,
      data,
    ),
  whatsAppActionReasons: (isEndReason: boolean) =>
    get<WhatsAppActionReason[]>('/conversations/action-reasons', {
      params: { isEndReason },
    }),
  whatsAppAgents: () => get<WhatsAppAgentOption[]>('/whatsapp/agents'),
  whatsAppAgentMappings: () => get<WhatsAppAgentMapping[]>('/whatsapp/agent-mappings'),
  whatsAppAssignableUsers: () => get<WhatsAppAssignableUser[]>('/whatsapp/assignable-users'),
  createWhatsAppAgentMapping: (data: { externalAdministratorId: number; mimAgentId: number }) =>
    send<WhatsAppAgentMapping>('post', '/whatsapp/agent-mappings', data),
  updateWhatsAppAgentMapping: (
    id: string,
    data: { externalAdministratorId: number; mimAgentId: number },
  ) => send<WhatsAppAgentMapping>('put', `/whatsapp/agent-mappings/${id}`, data),
  deleteWhatsAppAgentMapping: (id: string) => remove(`/whatsapp/agent-mappings/${id}`),
  legacyTickets: (params?: Record<string, string | number>) =>
    get<PagedResult<LegacyTicketListItem>>('/tickets', { params }),
  ticketSummary: () => get<TicketSummary>('/tickets/summary'),
  legacyTicket: (id: string | number) => get<LegacyTicketDetail>(`/tickets/${id}`),
  tickets: (params?: Record<string, string | number>) =>
    get<PagedResult<Ticket>>('/tickets', { params }),
  ticket: (id: string) => get<Ticket>(`/tickets/${id}`),
  ticketLookups: () => get<TicketLookups>('/tickets/lookups'),
  addTicketUpdate: (id: string | number, text: string, isPublic: boolean, files: File[]) => {
    const data = new FormData();
    data.append('text', text);
    data.append('isPublic', String(isPublic));
    files.forEach((file) => data.append('files', file));
    return send<LegacyTicketDetail>('post', `/tickets/${id}/updates`, data);
  },
  assignTicketTeam: (id: string | number, teamId: number) =>
    send<LegacyTicketDetail>('post', `/tickets/${id}/assign-team`, { teamId }),
  assignTicketUser: (id: string | number, administratorId: number) =>
    send<LegacyTicketDetail>('post', `/tickets/${id}/assign-user`, { administratorId }),
  changeTicketStatus: (id: string | number, status: number) =>
    send<LegacyTicketDetail>('post', `/tickets/${id}/status`, { status }),
  translateTicketText: (id: string | number, text: string, targetLanguage: 'ar' | 'en') =>
    send<{ translation: string; targetLanguage: string }>('post', `/tickets/${id}/translate`, {
      text,
      targetLanguage,
    }),
  createTicket: (data: Partial<Ticket>) => send<Ticket>('post', '/tickets', data),
  updateTicket: (id: string, data: Partial<Ticket>) =>
    send<Ticket>('patch', `/tickets/${id}`, data),
  callEnquiries: (params?: Record<string, string | number>) =>
    get<PagedResult<CallEnquiry>>('/calls', { params }),
  callEnquiryLookups: () => get<CallEnquiryLookups>('/calls/lookups'),
  createCallEnquiry: (data: CreateCallEnquiry) => send<CreatedCallEnquiry>('post', '/calls', data),
  articles: (params?: Record<string, string | number>) =>
    get<PagedResult<KnowledgeArticle>>('/knowledge/articles', { params }),
  knowledgeAnalytics: () => get<KnowledgeAnalytics>('/knowledge/analytics'),
  knowledgeArticleSummary: () => get<KnowledgeArticleSummary>('/knowledge/articles/summary'),
  article: (id: string) => get<KnowledgeArticle>(`/knowledge/articles/${id}`),
  createArticle: (data: Partial<KnowledgeArticle>) =>
    send<KnowledgeArticle>('post', '/knowledge/articles', data),
  updateArticle: (id: string, data: Partial<KnowledgeArticle>) =>
    send<KnowledgeArticle>('patch', `/knowledge/articles/${id}`, data),
  deleteArticle: (id: string) => remove(`/knowledge/articles/${id}`),
  resyncArticleToQdrant: (id: string) =>
    send<KnowledgeArticle>('post', `/knowledge/articles/${id}/resync-qdrant`, {}),
  resyncPublishedArticlesToQdrant: () =>
    send<{ total: number; succeeded: number; failed: number }>(
      'post',
      '/knowledge/articles/resync-qdrant',
      {},
    ),
  transitionArticle: (
    id: string,
    action: string,
    data: {
      expiresAt?: string | null | undefined;
      notes?: string | null | undefined;
      reviewGuardAcknowledged?: boolean | undefined;
    } = {},
  ) => send<KnowledgeArticle>('post', `/knowledge/articles/${id}/${action}`, data),
  createArticleTranslation: (id: string, language: string) =>
    send<KnowledgeArticle>('post', `/knowledge/articles/${id}/translations`, { language }),
  uploadArticleAttachments: (id: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return send<KnowledgeArticle>('post', `/knowledge/articles/${id}/attachments`, formData);
  },
  articleVersions: (id: string) =>
    get<KnowledgeArticle['versions']>(`/knowledge/articles/${id}/versions`),
  articleVersion: (id: string, version: number | string) =>
    get<KnowledgeArticleVersionDetail>(`/knowledge/articles/${id}/versions/${version}`),
  compareArticleVersionWithCurrent: (id: string, version: number | string) =>
    get<KnowledgeArticleVersionCompare>(
      `/knowledge/articles/${id}/versions/${version}/compare-current`,
    ),
  articleReviewGuard: (id: string) =>
    get<KnowledgeReviewGuard>(`/knowledge/articles/${id}/review-guard`),
  rerunArticleReviewGuard: (id: string) =>
    send<KnowledgeReviewGuard>('post', `/knowledge/articles/${id}/review-guard/rerun`, {}),
  previewArticleLanguageSync: async (id: string, targetLanguage: string, signal?: AbortSignal) =>
    (
      await apiClient.post<KnowledgeLanguageSyncPreview>(
        `/knowledge/articles/${id}/language-sync/preview`,
        { targetLanguage },
        {
          ...(signal ? { signal } : {}),
          timeout: 95_000,
          timeoutErrorMessage: 'AI language comparison timed out after 95 seconds.',
        },
      )
    ).data,
  applyArticleLanguageSync: (id: string, data: ApplyKnowledgeLanguageSyncRequest) =>
    send<KnowledgeLanguageSyncApplyResult>(
      'post',
      `/knowledge/articles/${id}/language-sync/apply`,
      data,
    ),
  addArticleFeedback: (id: string, data: { helpful: boolean; comment?: string }) =>
    send<KnowledgeArticle>('post', `/knowledge/articles/${id}/feedback`, data),
  knowledgeCategories: () => get<KnowledgeCategory[]>('/knowledge/categories'),
  createKnowledgeCategory: (data: Partial<KnowledgeCategory>) =>
    send<KnowledgeCategory>('post', '/knowledge/categories', data),
  updateKnowledgeCategory: (id: string, data: Partial<KnowledgeCategory>) =>
    send<KnowledgeCategory>('patch', `/knowledge/categories/${id}`, data),
  deleteKnowledgeCategory: (id: string) => remove(`/knowledge/categories/${id}`),
  knowledgeStatuses: () => get<KnowledgeLookup[]>('/knowledge/statuses'),
  knowledgeLanguages: () => get<KnowledgeLanguage[]>('/knowledge/languages'),
  knowledgeAudiences: () => get<KnowledgeAudience[]>('/knowledge/audiences'),
  createKnowledgeAudience: (data: Partial<KnowledgeAudience>) =>
    send<KnowledgeAudience>('post', '/knowledge/audiences', data),
  updateKnowledgeAudience: (id: string, data: Partial<KnowledgeAudience>) =>
    send<KnowledgeAudience>('patch', `/knowledge/audiences/${id}`, data),
  deleteKnowledgeAudience: (id: string) => remove(`/knowledge/audiences/${id}`),
  knowledgeTags: () => get<KnowledgeTag[]>('/knowledge/tags'),
  createKnowledgeTag: (data: Partial<KnowledgeTag>) =>
    send<KnowledgeTag>('post', '/knowledge/tags', data),
  deleteKnowledgeTag: (id: string) => remove(`/knowledge/tags/${id}`),
  knowledgeFeedback: (params?: Record<string, string | number>) =>
    get<PagedResult<KnowledgeFeedback>>('/knowledge/feedback', { params }),
  knowledgeGaps: (params?: Record<string, string | number>) =>
    get<KnowledgeGapPage>('/knowledge/gaps', { params }),
  knowledgeGapOccurrences: (id: string, params?: Record<string, string | number>) =>
    get<KnowledgeGapOccurrencePage>(`/knowledge/gaps/${id}/occurrences`, { params }),
  updateKnowledgeGap: (
    id: string,
    data: {
      status: KnowledgeGapStatus;
      assignedToUserId?: string | null;
      resolutionArticleId?: string | null;
      reviewNotes?: string | null;
      rowVersion: string;
    },
  ) => send<KnowledgeGap>('patch', `/knowledge/gaps/${id}`, data),
  knowledgePlaygroundChat: async (data: { sessionId?: string | null; message: string }) =>
    (
      await apiClient.post<KnowledgePlaygroundResponse>('/knowledge/playground/chat', data, {
        timeout: 90_000,
      })
    ).data,
  knowledgeWebhookSubscriptions: () =>
    get<KnowledgeWebhookSubscription[]>('/knowledge/connectors/webhooks'),
  createKnowledgeWebhookSubscription: (data: SaveKnowledgeWebhookSubscription) =>
    send<KnowledgeWebhookSubscription>('post', '/knowledge/connectors/webhooks', data),
  updateKnowledgeWebhookSubscription: (id: string, data: SaveKnowledgeWebhookSubscription) =>
    send<KnowledgeWebhookSubscription>('put', `/knowledge/connectors/webhooks/${id}`, data),
  setKnowledgeWebhookSubscriptionEnabled: (id: string, isEnabled: boolean) =>
    send<KnowledgeWebhookSubscription>('patch', `/knowledge/connectors/webhooks/${id}/enabled`, {
      isEnabled,
    }),
  disableKnowledgeWebhookSubscription: (id: string) =>
    remove(`/knowledge/connectors/webhooks/${id}`),
  knowledgeWebhookDeliveries: (params?: Record<string, string | number>) =>
    get<KnowledgeWebhookDeliveryPage>('/knowledge/connectors/webhooks/deliveries', { params }),
  replayKnowledgeWebhookDelivery: (id: string) =>
    send<KnowledgeWebhookDelivery>(
      'post',
      `/knowledge/connectors/webhooks/deliveries/${id}/replay`,
      {},
    ),
  knowledgeConnectors: () => get<KnowledgeConnector[]>('/knowledge/connectors'),
  createKnowledgeConnector: (data: SaveKnowledgeConnector) =>
    send<KnowledgeConnector>('post', '/knowledge/connectors', data),
  updateKnowledgeConnector: (id: string, data: SaveKnowledgeConnector) =>
    send<KnowledgeConnector>('put', `/knowledge/connectors/${id}`, data),
  disableKnowledgeConnector: (id: string) => remove(`/knowledge/connectors/${id}`),
  knowledgeConnectorCredentials: (id: string) =>
    get<KnowledgeConnectorCredential[]>(`/knowledge/connectors/${id}/credentials`),
  issueKnowledgeConnectorCredential: (
    id: string,
    data: { name: string; scopes: KnowledgeConnectorScope[]; expiresAtUtc: string | null },
  ) =>
    send<IssuedKnowledgeConnectorCredential>(
      'post',
      `/knowledge/connectors/${id}/credentials`,
      data,
    ),
  rotateKnowledgeConnectorCredential: (id: string, credentialId: string) =>
    send<IssuedKnowledgeConnectorCredential>(
      'post',
      `/knowledge/connectors/${id}/credentials/${credentialId}/rotate`,
      {},
    ),
  revokeKnowledgeConnectorCredential: (id: string, credentialId: string) =>
    remove(`/knowledge/connectors/${id}/credentials/${credentialId}`),
  knowledgeConnectorRuns: (params?: Record<string, string | number>) =>
    get<KnowledgeConnectorRunPage>('/knowledge/connectors/runs', { params }),
  discoverKnowledgeMcpSource: (id: string) =>
    send<KnowledgeMcpSourceCatalog>('post', `/knowledge/connectors/${id}/mcp-source/discover`, {}),
  syncKnowledgeMcpSource: (id: string, dryRun = false) =>
    send<KnowledgeMcpSourceSyncResponse>('post', `/knowledge/connectors/${id}/mcp-source/sync`, {
      dryRun,
    }),
  dryRunKnowledgeConnector: async (
    id: string,
    token: string,
    items: KnowledgeConnectorIngestItem[],
  ) =>
    (
      await apiClient.post<KnowledgeConnectorIngestResponse>(
        `/knowledge/connectors/${id}/ingest`,
        { items, dryRun: true },
        {
          helixAuth: 'connector',
          headers: {
            Authorization: `Bearer ${token}`,
            'Idempotency-Key': crypto.randomUUID(),
          },
        },
      )
    ).data,
  knowledgeMcpConnectors: () => get<KnowledgeMcpConnector[]>('/knowledge/mcp-connectors'),
  knowledgeMcpConnector: (id: string) =>
    get<KnowledgeMcpConnector>(`/knowledge/mcp-connectors/${id}`),
  createKnowledgeMcpConnector: (data: SaveKnowledgeMcpConnector) =>
    send<KnowledgeMcpConnector>('post', '/knowledge/mcp-connectors', data),
  updateKnowledgeMcpConnector: (id: string, data: SaveKnowledgeMcpConnector) =>
    send<KnowledgeMcpConnector>('put', `/knowledge/mcp-connectors/${id}`, data),
  disableKnowledgeMcpConnector: (id: string) => remove(`/knowledge/mcp-connectors/${id}`),
  knowledgeMcpCredentials: (id: string) =>
    get<KnowledgeMcpCredential[]>(`/knowledge/mcp-connectors/${id}/credentials`),
  knowledgeMcpConnectorAnalytics: (id: string, fromUtc: string, toUtc: string) =>
    get<KnowledgeMcpConnectorAnalytics>(`/knowledge/mcp-connectors/${id}/analytics`, {
      params: { fromUtc, toUtc },
    }),
  issueKnowledgeMcpCredential: (
    id: string,
    data: { name: string; channel: string; expiresAtUtc: string | null },
  ) =>
    send<IssuedKnowledgeMcpCredential>('post', `/knowledge/mcp-connectors/${id}/credentials`, data),
  rotateKnowledgeMcpCredential: (id: string, credentialId: string) =>
    send<IssuedKnowledgeMcpCredential>(
      'post',
      `/knowledge/mcp-connectors/${id}/credentials/${credentialId}/rotate`,
      {},
    ),
  revokeKnowledgeMcpCredential: (id: string, credentialId: string) =>
    remove(`/knowledge/mcp-connectors/${id}/credentials/${credentialId}`),
  knowledgeRestConnectors: () => get<KnowledgeMcpConnector[]>('/knowledge/rest-connectors'),
  knowledgeRestConnector: (id: string) =>
    get<KnowledgeMcpConnector>(`/knowledge/rest-connectors/${id}`),
  createKnowledgeRestConnector: (data: SaveKnowledgeMcpConnector) =>
    send<KnowledgeMcpConnector>('post', '/knowledge/rest-connectors', data),
  updateKnowledgeRestConnector: (id: string, data: SaveKnowledgeMcpConnector) =>
    send<KnowledgeMcpConnector>('put', `/knowledge/rest-connectors/${id}`, data),
  disableKnowledgeRestConnector: (id: string) => remove(`/knowledge/rest-connectors/${id}`),
  knowledgeRestCredentials: (id: string) =>
    get<KnowledgeMcpCredential[]>(`/knowledge/rest-connectors/${id}/credentials`),
  knowledgeRestConnectorAnalytics: (id: string, fromUtc: string, toUtc: string) =>
    get<KnowledgeMcpConnectorAnalytics>(`/knowledge/rest-connectors/${id}/analytics`, {
      params: { fromUtc, toUtc },
    }),
  issueKnowledgeRestCredential: (
    id: string,
    data: { name: string; channel: string; expiresAtUtc: string | null },
  ) =>
    send<IssuedKnowledgeMcpCredential>(
      'post',
      `/knowledge/rest-connectors/${id}/credentials`,
      data,
    ),
  rotateKnowledgeRestCredential: (id: string, credentialId: string) =>
    send<IssuedKnowledgeMcpCredential>(
      'post',
      `/knowledge/rest-connectors/${id}/credentials/${credentialId}/rotate`,
      {},
    ),
  revokeKnowledgeRestCredential: (id: string, credentialId: string) =>
    remove(`/knowledge/rest-connectors/${id}/credentials/${credentialId}`),
  escalations: (params?: Record<string, string | number>, signal?: AbortSignal) =>
    get<EscalationPage>('/escalations', { params, ...(signal ? { signal } : {}) }),
  whatsAppSla: () => get<WhatsAppSlaPolicy[]>('/sla/whatsapp'),
  updateWhatsAppSla: (
    id: number,
    data: Pick<
      WhatsAppSlaPolicy,
      'assignmentMinutes' | 'levelOneMinutes' | 'levelTwoMinutes' | 'version'
    >,
  ) => send<WhatsAppSlaPolicy>('patch', `/sla/whatsapp/${id}`, data),
  report: (id: string) => get<ReportResult>(`/reports/${id}`),
  users: (params?: Record<string, string | number>) =>
    get<IdentityPagedResult<IdentityUser>>('/users', { params }),
  assignableSystemAdministrators: () =>
    get<AssignableSystemAdministrator[]>('/users/assignable-system-administrators'),
  createIdentityUser: (data: {
    externalAdministratorId: number;
    roleIds: string[];
    permissionKeys: string[];
    status: string;
  }) => send<IdentityUser>('post', '/users', data),
  updateIdentityUser: (
    id: string,
    data: {
      displayName: string;
      email: string;
      status: string;
      roleIds: string[];
      permissionKeys: string[];
    },
  ) => send<IdentityUser>('put', `/users/${id}`, data),
  deactivateIdentityUser: (id: string) => remove(`/users/${id}`),
  roles: () => get<IdentityRole[]>('/roles'),
  identityPermissions: () => get<IdentityPermission[]>('/permissions'),
  createIdentityRole: (data: {
    name: string;
    description?: string | null;
    permissionKeys: string[];
  }) => send<IdentityRole>('post', '/roles', data),
  updateIdentityRole: (
    id: string,
    data: { name: string; description?: string | null; permissionKeys: string[] },
  ) => send<IdentityRole>('put', `/roles/${id}`, data),
  deleteIdentityRole: (id: string) => remove(`/roles/${id}`),
  teams: () => get<PagedResult<TeamRecord>>('/teams'),
  entities: () => get<PagedResult<EntityRecord>>('/entities'),
  channels: () => get<ChannelCatalogue>('/channels'),
  channelVisibility: () => get<ManagedChannelType[]>('/channels/visibility'),
  saveChannel: (id: string | undefined, data: SaveChannel) =>
    send<ChannelRecord>(id ? 'put' : 'post', id ? `/channels/${id}` : '/channels', data),
  setChannelVisibility: (code: ManagedChannelCode, enabled: boolean, version: string) =>
    send<ManagedChannelType>('patch', `/channels/types/${code}`, { enabled, version }),
  sla: () => get<PagedResult<SlaPolicy>>('/sla/policies'),
  updateSla: (id: string, data: Partial<SlaPolicy>) =>
    send<SlaPolicy>('patch', `/sla/policies/${id}`, data),
  settings: () => get<SettingsRecord>('/settings'),
  updateSettings: (data: Partial<SettingsRecord>) =>
    send<SettingsRecord>('patch', '/settings', data),
};
